import { MutableRefObject, useEffect, useRef } from "react"
import { cx, css } from "@emotion/css"

import { useGesture } from "react-use-gesture"

const styles = {
  root: css`
    height: 100%;
    width: 4px;
    background-color: var(--primary-main, teal);
    cursor: col-resize;
    user-select: none;
    touch-action: none;
    will-change: transform, opacity;
    opacity: 0;
    z-index: 2;
    position: absolute;
    right: 0;
    top: 0;

    :hover {
      opacity: 1;
    }
  `,
}

type ColumnResizerProps = {
  cellRef: MutableRefObject<HTMLDivElement | null>
  maxWidth: number
  minWidth: number
  onDone?: (newWidth: number) => void
}

export function ColumnResizer(props: ColumnResizerProps) {
  const { cellRef: parentRef, maxWidth, minWidth, onDone } = props
  const ref = useRef<HTMLDivElement | null>(null)
  const lastWidth = useRef<number>(0)

  useEffect(() => {
    if (!ref.current) return
    if (!parentRef.current) return
    const parentRect = parentRef.current.getBoundingClientRect()
    lastWidth.current = parentRect.width
  }, [parentRef])

  function handleDragEnd(dx: number) {
    if (!ref.current) return
    ref.current.style.position = "absolute"
    ref.current.style.left = "auto"
    ref.current.style.right = "0px"
    ref.current.style.top = "0px"
    const newWidth = lastWidth.current + dx
    if (newWidth < minWidth || newWidth > maxWidth || dx === 0) return
    lastWidth.current = newWidth
    onDone && onDone(newWidth)
  }

  function handleDrag(dx: number) {
    if (!ref.current) return
    if (!parentRef.current) return
    const parentRect = parentRef.current.getBoundingClientRect()
    ref.current.style.position = "fixed"
    ref.current.style.right = "auto"
    ref.current.style.left = `${dx}px`
    ref.current.style.top = `${parentRect.top}px`
    ref.current.style.height = `${parentRect.height}px`
  }

  const handlers = useGesture(
    {
      onDrag: ({ xy: [x] }) => handleDrag(x),
      onDragEnd: ({ movement: [mx] }) => handleDragEnd(mx),
    },
    { drag: { axis: "x", filterTaps: true } },
  )

  return (
    <div
      ref={ref}
      {...handlers()}
      className={cx("ColumnResizer", styles.root)}
    />
  )
}
